import { useCallback, useEffect, useRef, useState } from 'react';
import { archiveService, getService, updateService } from '../api/services.api';
import type { Service, UpdateServiceInput } from '../types/service.types';

export function useServiceWorkspace(id: string | undefined) {
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);

  const loadService = useCallback(() => {
    const generation = ++requestGenerationRef.current;
    const isCurrentRequest = () =>
      mountedRef.current && requestGenerationRef.current === generation;

    if (!id) {
      setService(null);
      setLoading(false);
      setError('شناسه سرویس نامعتبر است');
      return Promise.resolve();
    }

    setLoading(true);
    setError(null);

    return getService(id)
      .then((data) => {
        if (isCurrentRequest()) setService(data);
      })
      .catch(() => {
        if (isCurrentRequest()) setError('خطا در دریافت اطلاعات سرویس');
      })
      .finally(() => {
        if (isCurrentRequest()) setLoading(false);
      });
  }, [id]);

  const update = useCallback(async (input: UpdateServiceInput) => {
    if (!id) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      const data = await updateService(id, input);
      if (mountedRef.current) {
        setService(data);
        setActionSuccess('تغییرات سرویس با موفقیت ذخیره شد');
      }
    } catch {
      if (mountedRef.current) setActionError('خطا در به‌روزرسانی سرویس');
    } finally {
      if (mountedRef.current) setActionLoading(false);
    }
  }, [id]);

  const archive = useCallback(async (tenantId: number) => {
    if (!id) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      const data = await archiveService(id, tenantId);
      if (mountedRef.current) setService(data);
      await loadService();
      if (mountedRef.current) setActionSuccess('سرویس با موفقیت بایگانی شد');
    } catch {
      if (mountedRef.current) setActionError('خطا در بایگانی سرویس');
    } finally {
      if (mountedRef.current) setActionLoading(false);
    }
  }, [id]);

  useEffect(() => {
    mountedRef.current = true;
    void loadService();
    return () => {
      mountedRef.current = false;
    };
  }, [loadService]);

  return {
    service,
    loading,
    error,
    actionLoading,
    actionError,
    actionSuccess,
    update,
    archive,
    refetch: loadService,
  };
}
